import { Button } from '@creditornot/cb-button';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useCallback, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { updateMerchantAppLogin } from 'apis/restaurant-api/merchant-admin/venue/api';
import { Client, Role } from 'apis/restaurant-api/merchant-admin/venue/types';
import { APIErrorNotification, WoltModal } from 'common/components';

import RoleSelect, { isRole } from './RoleSelect';
import VenueRolesInfo from './VenueRolesInfo';

type Props = {
  isOpen: boolean;
  venueId: string;
  user: Client | null;
  onClose: () => void;
  onDone: () => void;
};

const Root = styled(WoltModal)`
  width: 100%;
  max-width: 480px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Actions = styled.div`
  display: flex;
`;

const StyledApiErrorNotification = styled(APIErrorNotification)`
  width: 100%;
  margin-block-end: 16px;
`;

const StyledVenueRolesInfo = styled(VenueRolesInfo)`
  margin-block-start: 24px;
`;

type FormValues = {
  role: Role | null;
};

const ChangeUserRightsModal = ({ isOpen, venueId, onClose, onDone, user }: Props) => {
  const initialValues: FormValues = useMemo(
    () => ({
      role: user?.role ?? null,
    }),
    [user?.role],
  );

  const handleSubmit = useCallback(
    async ({ role }: FormValues) => {
      if (!user || !role) {
        throw new Error('User is missing!');
      }
      await updateMerchantAppLogin(venueId, user.login, role);
      onDone();
      onClose();
    },
    [onDone, onClose, user, venueId],
  );

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="userManagement.changeRights" />
      </ModalHeader>
      <Form<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
        {({ handleSubmit, error, submitting, dirty }) => (
          <>
            <ModalContent>
              <Field<FormValues['role']> name="role">
                {field => (
                  <RoleSelect
                    {...field.input}
                    onChange={newRole => {
                      if (!isRole(newRole)) {
                        return;
                      }
                      field.input.onChange(newRole);
                    }}
                  />
                )}
              </Field>
              <StyledVenueRolesInfo />
            </ModalContent>
            <StyledModalFooter>
              {error && <StyledApiErrorNotification error={error} />}
              <Actions>
                <Button variant="secondary" size="small" onClick={onClose} disabled={submitting}>
                  <FormattedMessage id="state.cancel" />
                </Button>
                <Button
                  style={{ marginInlineStart: 16 }}
                  variant="primary"
                  size="small"
                  onClick={handleSubmit}
                  disabled={submitting || !dirty}
                  loading={submitting}
                >
                  <FormattedMessage id="state.save" />
                </Button>
              </Actions>
            </StyledModalFooter>
          </>
        )}
      </Form>
    </Root>
  );
};

export default ChangeUserRightsModal;
